import { AppDataProvider, BookingData, BookingTool } from "./AppDataProvider/AppDataProvider";
import { FocusTrap, emitEvent, getFirstStep, getWidgetType, useViewportSize } from "./utils";
import React, { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { getCustomFonts, getThemes, margin, media, paddingBottom, paddingTop, paddingX, resolveBrandThemeMode, useActiveBrandTheme } from "./common/styles";
import styled, { ThemeProvider, css } from "styled-components";
import { Footer } from "./Footer/Footer";
import { Header } from "./Header/Header";
import { Mixpanel } from "./mixpanel/mixpanel";
import { NotificationsAndDialogs } from "./NotificationsAndDialogs/NotificationsAndDialogs";
import { BrandHeadTags, BrandStyleTag, ProgressTracker } from "./common/components";
import { Steps } from "./Steps/Steps";
import { ThemeToggleContext } from "./ThemeToggleContext";
import { useTranslation } from "react-i18next";
type ContentContainerProps = {
  bookingData: BookingData;
  bookingTool: BookingTool;
  inPopoverMode?: boolean;
  userLocation?: string;
};
export function ContentContainer(props: ContentContainerProps): ReactElement {
  const {
    bookingTool,
    bookingData,
    inPopoverMode,
    userLocation
  } = props;
  const {
    i18n
  } = useTranslation();
  const viewport = useViewportSize();
  const [userHasClicked, setUserHasClicked] = useState(false);
  const recordFirstClick = (event: React.MouseEvent<HTMLElement>) => {
    if (userHasClicked) return;

    /**
     * We need to check if the first clicked element was the close button.
     * We use event.target.closest() as the event target could be the
     * .popoverCloseButton button itself or a child of the button.
     */
    if (event.target instanceof Element) {
      const targetIsCloseButtonOrChild = event.target.closest(".popoverCloseButton");
      /**
       * If the first selected element was the close button,
       * we do not want to emit the USER_ENGAGED event.
       */
      if (targetIsCloseButtonOrChild) return;
    }
    if (iFrameId) {
      emitEvent({
        data: {
          iFrameId: iFrameId
        },
        name: "USER_ENGAGED"
      });
    }
    Mixpanel.track({
      name: "Engaged"
    });
    setUserHasClicked(true);
  };
  useEffect(() => {
    const widgetOrganisations = bookingTool.organisations.edges.map(({
      node
    }) => node.slug);
    Mixpanel.register({
      initialLanguage: i18n.language,
      sessionId: bookingData.sessionId,
      widgetId: bookingTool._id,
      widgetOrganisations,
      widgetType: getWidgetType(bookingData, inPopoverMode)
    });
    Mixpanel.track({
      name: "App loaded"
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const progressInHeader = viewport.width >= media.thresholds.progressInHeader && viewport.width < media.thresholds.desktop;
  const urlParams = new URLSearchParams(window.location.search);
  const iFrameId = urlParams.get("iFrameId") ?? undefined;
  const reservationOnly = urlParams.get("reservationOnly") ? true : false;
  const {
    customColours,
    brand,
    fonts
  } = bookingTool;
  const {
    customFonts,
    customFontFaces
  } = getCustomFonts(fonts);
  const {
    light,
    dark
  } = getThemes({
    customColours,
    brand,
    customFonts
  });
  const allowVisitorThemeOverride = brand?.allowVisitorThemeOverride ?? true;
  const {
    activeThemeName,
    setVisitorOverride
  } = useActiveBrandTheme({
    allowVisitorThemeOverride,
    brandThemeMode: resolveBrandThemeMode(brand)
  });
  const theme = activeThemeName === "dark" ? dark : light;
  const themeToggleContextValue = useMemo(() => ({
    activeThemeName,
    allowVisitorThemeOverride,
    setVisitorOverride
  }), [activeThemeName, allowVisitorThemeOverride, setVisitorOverride]);
  const {
    pageBackground
  } = theme.brand;
  const resolvedPageBackground = pageBackground ? /^https?:\/\//.test(pageBackground) ? `url(${pageBackground}) center / cover no-repeat` : pageBackground : undefined;

  /**
   * Whether the widget content (Header/BodyContainer/Footer) needs its own
   * distinct solid "card" shell, separate from `Container`'s own
   * background. When a brand has configured a custom `pageBackground`
   * (gradient/colour/image), `Container` shows that as the outermost page
   * backdrop (see below) — so the content needs an explicit `--d-surface`
   * shell on top of it to stay a legible, distinct surface, rather than
   * every unstyled bit of Header/Footer/Steps text sitting directly on the
   * raw gradient. Popover mode already gets an equivalent solid/bordered
   * shell from `Container` itself (see the `inPopoverMode` branch below),
   * so this only applies to the non-popover ("microsite"/embed) case.
   *
   * `theme.brand.pageBackground` is never actually unset by the time it
   * reaches here: `resolveBrandPalette` (see
   * `src/shared/node/BrandConfig/resolveBrandPalette.ts`) always falls
   * back to Appointedd's static default palette for any field a brand
   * hasn't customised, and that static default itself sets a flat
   * `pageBackground` (`#ffffff`/`#17151b`) equal to the theme's own
   * default surface colour — so a plain truthy check here would show this
   * card shell for EVERY brand, customised or not. Comparing against the
   * theme's resolved surface colour (`theme.colours.white`, i.e.
   * `--d-surface`) recovers the "did a brand actually configure a
   * distinct backdrop" signal: a flat colour/URL background that matches
   * the surface colour is visually indistinguishable from "no custom
   * background", so no extra shell/shadow is needed in that case either.
   */
  const showContentSurface = !inPopoverMode && !!resolvedPageBackground && resolvedPageBackground !== theme.colours.white;
  const currentStep = getFirstStep({
    bookingData: bookingData,
    bookingTool: bookingTool
  });
  const scrollableRef = useRef<HTMLDivElement>(null);
  const scrollToTop = () => {
    if (userHasClicked && scrollableRef?.current?.scrollIntoView) {
      scrollableRef.current.scrollIntoView(true);
    }
  };
  return <>
      <style>{customFontFaces}</style>

      <FocusTrap active={inPopoverMode} data-sentry-element="FocusTrap" data-sentry-source-file="ContentContainer.tsx">
        <Container data-testid="appContainer" data-brand-theme={theme.brand.theme} onClick={e => recordFirstClick(e)} inPopoverMode={inPopoverMode} pageBackground={resolvedPageBackground} brandTheme={theme.brand.theme} ref={scrollableRef} data-sentry-element="Container" data-sentry-source-file="ContentContainer.tsx">
          <ThemeProvider theme={theme} data-sentry-element="ThemeProvider" data-sentry-source-file="ContentContainer.tsx">
            <ThemeToggleContext.Provider value={themeToggleContextValue} data-sentry-element="unknown" data-sentry-source-file="ContentContainer.tsx">
              <BrandStyleTag theme={theme} data-sentry-element="BrandStyleTag" data-sentry-source-file="ContentContainer.tsx" />
              <BrandHeadTags faviconUrl={brand?.faviconUrl} pageTitleSuffix={brand?.pageTitleSuffix} data-sentry-element="BrandHeadTags" data-sentry-source-file="ContentContainer.tsx" />
              <AppDataProvider iFrameId={iFrameId} bookingTool={bookingTool} bookingData={bookingData} currentStep={currentStep} userLocation={userLocation} reservationOnly={reservationOnly} data-sentry-element="AppDataProvider" data-sentry-source-file="ContentContainer.tsx">
                <NotificationsAndDialogs data-sentry-element="NotificationsAndDialogs" data-sentry-source-file="ContentContainer.tsx" />
                {showContentSurface ? <ContentSurface data-testid="contentSurface" brandTheme={theme.brand.theme}>
                    <Header />
                    <BodyContainer>
                      {!progressInHeader && <ProgressTracker />}
                      <Steps scrollToTop={scrollToTop} />
                    </BodyContainer>
                    {!bookingTool.hideAppointeddFooter && <Footer />}
                  </ContentSurface> : <>
                    <Header />
                    <BodyContainer>
                      {!progressInHeader && <ProgressTracker />}
                      <Steps scrollToTop={scrollToTop} />
                    </BodyContainer>
                    {!bookingTool.hideAppointeddFooter && <Footer />}
                  </>}
              </AppDataProvider>
            </ThemeToggleContext.Provider>
          </ThemeProvider>
        </Container>
      </FocusTrap>
    </>;
}
const Container = styled.div<{
  inPopoverMode?: boolean;
  pageBackground?: string;
  brandTheme?: "light" | "dark";
}>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  align-self: center;

  /*
   * The brand pageBackground (a gradient/colour/image) is the OUTERMOST
   * page backdrop only. It must be painted here, underneath everything
   * else, and never override the solid --d-surface content shell below —
   * otherwise every "content area" (Header, BodyContainer, Footer, and any
   * component that doesn't set its own explicit --d-card background, e.g.
   * plain text) ends up sitting directly on top of the raw gradient
   * instead of a distinct, readable content surface, while components that
   * DO set their own background (BookingSummaryCard, inputs) look like
   * mismatched patches of a completely different colour floating on it.
   *
   * In popover mode there is no separate "page" for the widget to own —
   * the widget IS the popover card, floating over the HOST site's own
   * page (dimmed by RootContainer's pinned neutral scrim). So pageBackground
   * is ignored entirely here when inPopoverMode is true: the card always
   * gets a flat --d-surface fill, matching the plain/neutral popover card
   * chrome in the reference design. Without this, the card interior showed
   * the raw brand gradient, which both looked inconsistent with the
   * reference and made flat-filled child elements (e.g. unavailable
   * CalendarDatePicker date cells, which paint a flat --d-surface
   * background) stand out as visible mismatched rectangles against the
   * gradient's varying tone.
   */
  ${({
  pageBackground,
  inPopoverMode
}) => pageBackground && !inPopoverMode ? css`
          background: ${pageBackground};
        ` : css`
          background-color: var(--d-surface);
        `}

  ${({
  inPopoverMode,
  brandTheme
}) => inPopoverMode ? css`
          overflow: scroll;
          border: 1px solid var(--d-border);
          border-radius: 14px;
          box-shadow: ${brandTheme === "dark" ? "0 24px 60px rgba(0,0,0,.5)" : "0 12px 32px rgba(17,17,17,.12)"};
          ${paddingTop.s}
          ${paddingX.m}
          @media screen and (width >= 1200px) {
            ${paddingTop.m}
            ${paddingX.l}
          }
        ` : null}
`;
const ContentSurface = styled.div<{
  brandTheme?: "light" | "dark";
}>`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  background-color: var(--d-surface);
  border: 1px solid var(--d-border);
  border-radius: 14px;
  box-shadow: ${({
  brandTheme
}) => brandTheme === "dark" ? "0 24px 60px rgba(0,0,0,.5)" : "0 12px 32px rgba(17,17,17,.12)"};
  ${margin.s}
  ${paddingTop.s}
  ${paddingX.m}
  ${paddingBottom.s}
  @media screen and (width >= 1200px) {
    ${margin.m}
    ${paddingTop.m}
    ${paddingX.l}
  }
`;
const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  /*
   * paddingX.s (8px each side) ensures every full-width interactive element
   * (e.g. Continue/Back buttons and form inputs on mobile) has enough
   * horizontal clearance for the WCAG 2.4.7 focus ring (which extends 4px
   * outside the element's border box) to render fully without being clipped
   * at the left/right edges of the widget frame. Without this, the ring
   * bleeds flush to the boundary on narrow viewports where buttons stretch
   * to 100% width.
   *
   * Note: BodyContainer also renders inside ContentSurface (branded-background
   * variant) and inside the popover Container, both of which apply their own
   * outer paddingX.m/l. The combined inset is slightly larger in those paths,
   * but still visually correct — the WCAG ring clearance is the minimum needed
   * and ample in all cases.
   */
  ${paddingX.s}
  ${paddingBottom.s}
  @media screen and (width >= 1200px) {
    ${paddingX.m}
    ${paddingBottom.m}
  }
  @media ${media.desktop} {
    flex-direction: row;
    align-items: flex-start;
  }
`;
export default ContentContainer;